import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MathRenderer from '../common/MathRenderer';
import { detailAnswerService } from '../../services/detailAnswerService';
import api from '../../services/api';
import { getQuestionImageDisplayUrl, QuestionImage } from '../common/QuestionImage';

const ViewQuestionDialog = ({ open, onClose, question }) => {
  const [detailAnswer, setDetailAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fullQuestion, setFullQuestion] = useState(null);

  useEffect(() => {
    if (open && question?.id) {
      loadFullQuestion();
      loadDetailAnswer();
    } else {
      setDetailAnswer(null);
      setFullQuestion(null);
    }
  }, [open, question?.id]);

  const loadFullQuestion = async () => {
    if (!question?.id) return;
    try {
      const res = await api.get(`/exams/questions/${question.id}/`);
      setFullQuestion(res.data);
    } catch (err) {
      console.error('Error loading question details:', err);
      setFullQuestion(null);
    }
  };

  const loadDetailAnswer = async () => {
    if (!question?.id) return;
    
    setLoading(true);
    try {
      const detailAnswers = await detailAnswerService.getByQuestionId(question.id);
      if (detailAnswers && detailAnswers.length > 0) {
        setDetailAnswer(detailAnswers[0]);
      } else {
        setDetailAnswer(null);
      }
    } catch (error) {
      console.error('Error loading detail answer:', error);
      setDetailAnswer(null);
    } finally {
      setLoading(false);
    }
  };

  const formatOptions = (options) => {
    if (!options || !Array.isArray(options)) return [];
    return options;
  };

  if (!question) return null;

  const displayQuestion = fullQuestion ? { ...question, ...fullQuestion } : question;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">View Question #{displayQuestion.id}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Section 1: Question Details */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'primary.main' }}>
              Question Details
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ID
                </Typography>
                <Typography variant="body1">{displayQuestion.id}</Typography>
              </Box>

              <Divider />

              {displayQuestion.subject_name && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Subject
                    </Typography>
                    <Typography variant="body1">{displayQuestion.subject_name}</Typography>
                  </Box>
                  <Divider />
                </>
              )}

              {displayQuestion.topic_name && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Topic
                    </Typography>
                    <Typography variant="body1">{displayQuestion.topic_name}</Typography>
                  </Box>
                  <Divider />
                </>
              )}

              {displayQuestion.academic_years && displayQuestion.academic_years.length > 0 && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Boards & Academic Years
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {displayQuestion.academic_years.map((ay, idx) => (
                        <Chip
                          key={idx}
                          label={`${ay.board_name || ay.board || 'N/A'} - Year ${ay.year || 'N/A'}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Divider />
                </>
              )}

              {displayQuestion.difficulty && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Difficulty
                    </Typography>
                    <Chip label={displayQuestion.difficulty} size="small" color="primary" />
                  </Box>
                  <Divider />
                </>
              )}

              {displayQuestion.question_type && (
                <>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Question Type
                    </Typography>
                    <Typography variant="body1">{displayQuestion.question_type}</Typography>
                  </Box>
                  <Divider />
                </>
              )}

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Question Text
                </Typography>
                <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <MathRenderer content={displayQuestion.question_text} variant="body1" />
                </Box>
              </Box>

              {(displayQuestion.image_proxy_url || displayQuestion.image_url) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Question Image
                    </Typography>
                    <QuestionImage question={displayQuestion} alt="question" />
                  </Box>
                </>
              )}

              {displayQuestion.options && formatOptions(displayQuestion.options).length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Options
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {formatOptions(displayQuestion.options).map((opt, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold', minWidth: '30px' }}>
                            {String.fromCharCode(65 + idx)}.
                          </Typography>
                          <MathRenderer content={opt} variant="body2" />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>

          {/* Section 2: Correct Answer or Sub-Questions */}
          {!displayQuestion.has_subquestions ? (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'success.main' }}>
                Correct Answer
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
                <MathRenderer content={displayQuestion.correct_answer || 'No answer provided'} variant="body1" />
              </Box>
            </Paper>
          ) : displayQuestion.sub_questions && displayQuestion.sub_questions.length > 0 ? (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'success.main' }}>
                Sub-Questions
              </Typography>
              <Stack spacing={2}>
                {displayQuestion.sub_questions.map((subQ) => (
                  <Box key={subQ.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ fontWeight: 600, mb: 1 }}>
                      <MathRenderer
                        content={(subQ.letter || String.fromCharCode(97 + displayQuestion.sub_questions.indexOf(subQ))) + '. ' + (subQ.question_text || '')}
                        variant="subtitle2"
                      />
                    </Box>
                    {(subQ.image_proxy_url || subQ.image_url) && (
                      <QuestionImage question={subQ} alt={`Sub-question ${subQ.letter || ''}`} />
                    )}
                    {subQ.options && subQ.options.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Options:</Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {subQ.options.map((opt, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: opt === subQ.correct_answer ? 'success.light' : 'grey.50',
                                border: opt === subQ.correct_answer ? '1px solid' : '1px solid transparent',
                                borderColor: opt === subQ.correct_answer ? 'success.main' : 'transparent'
                              }}
                            >
                              <MathRenderer content={`${idx + 1}. ${opt}`} variant="body2" />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">Correct Answer: </Typography>
                      <MathRenderer content={subQ.correct_answer || ''} variant="body2" sx={{ fontWeight: 600 }} />
                    </Box>
                    {(subQ.detail_answer || subQ.detail_answer_image_proxy_url || subQ.detail_answer_image) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Detail Answer:</Typography>
                        {subQ.detail_answer && (
                          <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <MathRenderer content={subQ.detail_answer} variant="body2" />
                          </Box>
                        )}
                        {(subQ.detail_answer_image_proxy_url || subQ.detail_answer_image) && (
                          <Box sx={{ mt: 1 }}>
                            <img
                              src={subQ.detail_answer_image_proxy_url || subQ.detail_answer_image}
                              alt={`Sub-question ${subQ.letter || ''} detail answer`}
                              style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 4 }}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Marks: </Typography>
                      <Typography variant="body2" component="span">
                        {subQ.marks}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ) : null}

          {/* Section 3: Detail Answer */}
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'info.main' }}>
              Detail Answer
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading detail answer...</Typography>
              </Box>
            ) : detailAnswer ? (
              <Stack spacing={2}>
                {detailAnswer.answer && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Answer Text
                    </Typography>
                    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <MathRenderer content={detailAnswer.answer} variant="body1" />
                    </Box>
                  </Box>
                )}

                {(detailAnswer.answer_image || detailAnswer.answer_image_proxy_url) && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Answer Image
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={detailAnswer.answer_image_proxy_url || detailAnswer.answer_image}
                        alt="detail answer"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {!detailAnswer.answer && !detailAnswer.answer_image && !detailAnswer.answer_image_proxy_url && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No detail answer available for this question.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No detail answer available for this question.
              </Typography>
            )}
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewQuestionDialog;

